import { ActionPermissionSetFragment } from "@src/features/roles/dialogs/NewRoleDialog.generated";
import {
  UserActionPermissionValueFragment,
  UserActionPermissionsFragment,
} from "@src/lib/contexts/ActionPermissionsContext.generated";
import {
  ActionPermissionSetInput,
  Ff,
  OptionalBooleanValue,
  UserType,
} from "@src/types.generated";
import { FeatureFlagContext } from "@src/lib/contexts/FeatureFlagContext";
import { resolveFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { useContext } from "react";

export type UserActionPermissionsKey = keyof Omit<
  UserActionPermissionsFragment,
  "__typename"
>;

export const isUserActionPermissionValue = (
  value: UserActionPermissionValueFragment | string,
): value is UserActionPermissionValueFragment => {
  return (
    (value as UserActionPermissionValueFragment).__typename ===
    "UserActionPermissionValue"
  );
};

export type PermissionDetails = {
  name: string;
  description?: string;
  showWhenFeatureFlagsEnabled?: Ff[];
  hideWhenFeatureFlagsEnabled?: Ff[];
  /** Marks a recently added permission with a "New" tag. */
  isNew?: boolean;
};

export const USER_ACTION_PERMISSIONS_NAME_AND_DESCRIPTION: Record<
  UserActionPermissionsKey,
  PermissionDetails
> = {
  changeOrgSettings: {
    name: "Change Org Settings",
    description: "Manage roles, locations, brand, change user type, etc",
  },
  editTrainees: {
    name: "Edit Trainees",
    description: "Edit someone's name, roles, locations, etc",
  },
  deactivateTrainees: {
    name: "Deactivate Trainees",
    description: "Deactivate or reactivate a user",
  },
  manageOrgIntegrations: {
    name: "Manage Org Integrations",
    description: "Manage integrations like HRIS",
  },
  manageBilling: {
    name: "Manage Billing",
    description: "Configure payment method / view billing history",
  },
  inviteOthers: {
    name: "Invite Others",
    description: "Invite new users to the org",
  },
  createContent: {
    name: "Create Content",
    description: "Create courses, modules, files, or check-ins",
  },
  manageLibrary: {
    name: "Manage Library",
    description: "Manage tags, archive/unarchive content",
  },
  assignContent: {
    name: "Assign Content",
    description: "Manually assign any non-premium content",
  },
  unassignContent: {
    name: "Unassign Content",
    description: "Manually unassign any non-premium content",
  },
  updateAutomations: {
    name: "Update Automations and Library Access",
    description: "Manage content automations",
  },
  verifySkills: {
    name: "Complete all check-ins",
    description: "Can complete any check-ins at their locations",
  },
  viewReporting: {
    name: "View Reporting",
    description: "View and export reporting data",
  },
  sendMessages: {
    name: "Send Messages",
    description: "Send messages in the dashboard and mobile app",
    hideWhenFeatureFlagsEnabled: [Ff.NewMessaging],
  },
  sendPosts: {
    name: "Send Posts",
    description: "Send Newsfeed posts.",
    showWhenFeatureFlagsEnabled: [Ff.NewMessaging],
  },
  managePremiumContent: {
    name: "Manage Premium Content",
    description: "Manage content like Sexual Harassment Prevention Training",
  },
  promoteTrainees: {
    name: "Promote Trainees",
    description: "Promote Trainees to Manager  in Opus",
  },
  markAsComplete: {
    name: "Mark as Complete",
    description: "Mark any content as complete",
  },
  viewAllSubmittedTaskLists: {
    name: "View all Checklist submissions",
    description: "View all Checklist submissions across all locations",
  },
  createChat: {
    name: "Start Messages",
    description:
      "Start Direct and Group Messages. Trainees and Managers can only message people at their locations.",
    showWhenFeatureFlagsEnabled: [Ff.NewMessaging],
  },
  impersonateUsers: {
    name: "View as others",
    description:
      "See Opus exactly as someone this role can already see, read-only. Includes their direct messages and drafts.",
    showWhenFeatureFlagsEnabled: [Ff.Impersonation],
    isNew: true,
  },
};

export const useUserActionPermissionDetails = (): Record<
  UserActionPermissionsKey,
  PermissionDetails
> => USER_ACTION_PERMISSIONS_NAME_AND_DESCRIPTION;

const PERMISSION_KEYS_WITH_NAME = Object.keys(
  USER_ACTION_PERMISSIONS_NAME_AND_DESCRIPTION,
) as UserActionPermissionsKey[];

export const useIsUserActionPermissionVisible = (): ((
  key: UserActionPermissionsKey,
) => boolean) => {
  const flags = useContext(FeatureFlagContext);
  return (key: UserActionPermissionsKey) => {
    const { showWhenFeatureFlagsEnabled, hideWhenFeatureFlagsEnabled } =
      USER_ACTION_PERMISSIONS_NAME_AND_DESCRIPTION[key];
    if (
      hideWhenFeatureFlagsEnabled?.length &&
      hideWhenFeatureFlagsEnabled.every((flag) =>
        resolveFeatureFlag(flags, flag),
      )
    ) {
      return false;
    }
    return (showWhenFeatureFlagsEnabled || []).every((flag) =>
      resolveFeatureFlag(flags, flag),
    );
  };
};

type RequiredAndOptionalRolePermissions = {
  required: UserActionPermissionsKey[];
  optionalDefaultChecked: UserActionPermissionsKey[];
  optionalDefaultUnchecked?: UserActionPermissionsKey[];
};

export const USER_TYPE_TO_PERMISSIONS: Record<
  UserType,
  RequiredAndOptionalRolePermissions
> = {
  [UserType.Admin]: {
    required: ["editTrainees", "deactivateTrainees"],
    optionalDefaultChecked: [
      "changeOrgSettings",
      "manageOrgIntegrations",
      "manageBilling",
      "inviteOthers",
      "createContent",
      "manageLibrary",
      "assignContent",
      "unassignContent",
      "updateAutomations",
      "verifySkills",
      "viewReporting",
      "sendMessages",
      "sendPosts",
      "managePremiumContent",
      "promoteTrainees",
      "markAsComplete",
      "createChat",
      "impersonateUsers",
    ],
  },
  [UserType.Manager]: {
    required: ["inviteOthers"],
    optionalDefaultChecked: [
      "createContent",
      "manageLibrary",
      "assignContent",
      "unassignContent",
      "verifySkills",
      "viewReporting",
      "sendMessages",
      "editTrainees",
      "deactivateTrainees",
      "promoteTrainees",
    ],
    optionalDefaultUnchecked: [
      "markAsComplete",
      "viewAllSubmittedTaskLists",
      "createChat",
      "sendPosts",
      "impersonateUsers",
    ],
  },
  [UserType.Employee]: {
    required: [],
    optionalDefaultChecked: ["verifySkills"],
    optionalDefaultUnchecked: ["createChat", "sendPosts"],
  },
};

/**
 * Whether a permission shows as ticked for a role of `userType`, as the
 * server resolves it. View as others left unset (null/undefined) is on for
 * Admin roles and off for Manager roles; every other unset permission is off.
 * Only a toggle writes an explicit value, so an untouched role stays unset.
 */
export const isPermissionTicked = (
  permissions: ActionPermissionSetInput,
  permission: UserActionPermissionsKey,
  userType: UserType | undefined,
): boolean => {
  const value = permissions[permission]?.value;
  if (value == null && permission === "impersonateUsers") {
    return userType === UserType.Admin;
  }
  return Boolean(value);
};

export const createActionPermissionSetInputWithDefault = (
  defaultValue: boolean | undefined,
): ActionPermissionSetInput => {
  const input = PERMISSION_KEYS_WITH_NAME.reduce(
    (
      acc: Partial<{ [key in UserActionPermissionsKey]: OptionalBooleanValue }>,
      key: UserActionPermissionsKey,
    ) => {
      acc[key] = {
        value: defaultValue,
      };
      return acc;
    },
    {},
  ) as { [key in UserActionPermissionsKey]: OptionalBooleanValue };
  return input;
};

export const createDefaultActionPermissionSetInputForUserType = (
  userType?: UserType,
): ActionPermissionSetInput => {
  // View as others stays unset, so the server's default for the role type
  // applies (on for Admin roles, off for others) until someone toggles it.
  const input: ActionPermissionSetInput = {
    ...createActionPermissionSetInputWithDefault(false),
    impersonateUsers: { value: undefined },
  };
  if (!userType) {
    return input;
  }
  const { required, optionalDefaultChecked, optionalDefaultUnchecked } =
    USER_TYPE_TO_PERMISSIONS[userType];
  const checkedKeys = [...required, ...optionalDefaultChecked];
  const uncheckedKeys = optionalDefaultUnchecked || [];
  const newInput = { ...input };
  if (userType === UserType.Employee) {
    return newInput;
  }
  checkedKeys.forEach((key) => {
    newInput[key] = {
      value: true,
    };
  });
  uncheckedKeys.forEach((key) => {
    newInput[key] = {
      value: false,
    };
  });
  newInput.impersonateUsers = { value: undefined };
  return newInput;
};

export const actionPermissionsFragmentToInput = (
  actionPermissionSet?: ActionPermissionSetFragment,
): ActionPermissionSetInput => {
  // Base on "inherit" rather than all-false so a permission the fragment
  // doesn't select (e.g. one added after this fragment was last updated)
  // defaults to inherit instead of being silently denied.
  const input = { ...createActionPermissionSetInputWithDefault(undefined) };
  if (!actionPermissionSet) {
    return input;
  }
  Object.entries(actionPermissionSet).forEach(([key, value]) => {
    if (key in USER_ACTION_PERMISSIONS_NAME_AND_DESCRIPTION) {
      input[key as UserActionPermissionsKey] = {
        value: typeof value === "boolean" ? value : undefined,
      };
    }
  });
  return input as ActionPermissionSetInput;
};
